import type { LucideIcon } from 'lucide-react';
import {
  MessageSquare,
  FolderOpen,
  Mic,
  FileText,
  FileCheck,
  FileSignature,
  FileStack,
  Package,
  MessageCircle,
  BookMarked,
  Users,
  Settings,
  ChevronDown,
} from 'lucide-react';
import { FEATURE_LITIGATION } from '../../constants';

/** Alle Portal-Ansichten (intern + Navigation). */
export type PortalView =
  | 'home'
  | 'chat'
  | 'dokumente'
  | 'diktat'
  | 'upload'
  | 'protokolle'
  | 'bausteine'
  | 'litigation'
  | 'vertrags_qa'
  | 'contract_generator'
  | 'document_editor'
  | 'pdf_tools'
  | 'gedaechtnis'
  | 'feedback'
  | 'matrix'
  | 'settings'
  | 'kanzlei_zugaenge';

/** Diktat-Reiter: Aufnehmen (Mikrofon) | Hochladen | Handy/Element. */
export type DiktatTab = 'record' | 'upload' | 'anleitung';

/** Unter-Reiter im Dokumente-Bereich bei geöffnetem Editor (OnlyOffice + ARKO). */
export type DokumenteAreaTab = 'editor' | 'hub';

export interface NavItemConfig {
  id: PortalView;
  icon: LucideIcon;
  label: string;
  /** Nur in „Mehr“ — dezenter Hinweis */
  dot?: boolean;
  badge?: 'beta' | 'neu';
}

export interface PortalNavContext {
  isPremium: boolean;
  contractGeneratorEnabled: boolean;
  /** MEMCORE Modul B (Labor): „Mein Gedächtnis“ + Speicher-Flows sichtbar. */
  memcoreEnabled?: boolean;
  /** Professional/Premium IT-Admin / primärer Account — Sidebar „Kanzlei & Zugänge“. */
  teamAccessEnabled?: boolean;
  /** Beta-Tester: Feedback prominent in der Primärnav. */
  betaTester?: boolean;
}

/** PB-Feld matrixRoomId — Raum für Element-Deep-Link. */
export function hasMatrixRoom(matrixRoomId: string | undefined | null): boolean {
  return Boolean(String(matrixRoomId ?? '').trim());
}

/** Primäre Sidebar — ARKO · Diktat · Dokumente. ARKO-Dokument-Split nur bei geöffnetem Editor. */
export function getPrimaryNavItems(ctx?: Pick<PortalNavContext, 'betaTester'>): NavItemConfig[] {
  const items: NavItemConfig[] = [
    { id: 'chat', icon: MessageSquare, label: 'ARKO' },
    { id: 'diktat', icon: Mic, label: 'Diktat' },
    { id: 'dokumente', icon: FolderOpen, label: 'Dokumente' },
  ];
  if (ctx?.betaTester) {
    items.push({
      id: 'feedback',
      icon: MessageCircle,
      label: 'Beta-Feedback',
      badge: 'beta',
    });
  }
  return items;
}

/** Konto-Footer — Matrix & Einstellungen (nicht Hauptnav). */
export function getAccountNavItems(): NavItemConfig[] {
  return [
    { id: 'matrix', icon: Users, label: 'Matrix' },
    { id: 'settings', icon: Settings, label: 'Einstellungen' },
  ];
}

/** Einklappbare Zusatznavigation. */
export function getMehrNavItems(ctx: PortalNavContext): NavItemConfig[] {
  const items: NavItemConfig[] = [];
  if (ctx.teamAccessEnabled) {
    items.push({ id: 'kanzlei_zugaenge', icon: Users, label: 'Kanzlei & Zugänge' });
  }
  if (ctx.isPremium) {
    items.push({ id: 'litigation', icon: FileText, label: FEATURE_LITIGATION, dot: true });
    items.push({ id: 'vertrags_qa', icon: FileCheck, label: 'Vertrags-QA', badge: 'neu' });
  }
  if (ctx.isPremium && ctx.contractGeneratorEnabled) {
    items.push({ id: 'contract_generator', icon: FileSignature, label: 'Vertrag erstellen', badge: 'beta' });
  }
  if (ctx.isPremium && ctx.memcoreEnabled) {
    items.push({ id: 'gedaechtnis', icon: BookMarked, label: 'Mein Gedächtnis', badge: 'beta' });
  }
  items.push({ id: 'pdf_tools', icon: FileStack, label: 'PDF-Werkzeuge', badge: 'neu' });
  items.push({ id: 'bausteine', icon: Package, label: 'Bausteine' });
  if (!ctx.betaTester) {
    items.push({ id: 'feedback', icon: MessageCircle, label: 'Feedback' });
  }
  return items;
}

/** Minimaler Titel — keine Marketing-Subtitles in der Hauptfläche. */
export function portalViewTitle(view: PortalView): string {
  switch (view) {
    case 'home':
      return 'Start';
    case 'chat':
      return 'ARKO';
    case 'dokumente':
    case 'protokolle':
      return 'Dokumente';
    case 'diktat':
    case 'upload':
      return 'Diktat';
    case 'bausteine':
      return 'Bausteine';
    case 'litigation':
      return FEATURE_LITIGATION;
    case 'vertrags_qa':
      return 'Vertrags-QA';
    case 'contract_generator':
      return 'Vertrag erstellen';
    case 'document_editor':
      return 'Dokument';
    case 'pdf_tools':
      return 'PDF-Werkzeuge';
    case 'gedaechtnis':
      return 'Mein Gedächtnis';
    case 'feedback':
      return 'Feedback';
    case 'matrix':
      return 'Matrix';
    case 'settings':
      return 'Einstellungen';
    case 'kanzlei_zugaenge':
      return 'Kanzlei & Zugänge';
    default:
      return 'ARKORBIT Portal';
  }
}

/** Welche View in der Sidebar als aktiv gilt. */
export function resolveSidebarActiveView(view: PortalView): PortalView {
  if (view === 'protokolle' || view === 'document_editor') return 'dokumente';
  if (view === 'upload') return 'diktat';
  return view;
}

/** Views in „Mehr“, die das Untermenü offen halten. */
export const MEHR_VIEWS: PortalView[] = [
  'kanzlei_zugaenge',
  'litigation',
  'vertrags_qa',
  'contract_generator',
  'gedaechtnis',
  'pdf_tools',
  'bausteine',
  'feedback',
];

export function isMehrViewActive(view: PortalView): boolean {
  return MEHR_VIEWS.includes(view);
}

/** Chevron für Mehr-Gruppe (re-export für Sidebar). */
export { ChevronDown };

/** Persistiert in sessionStorage (Tab-Reload, kein localStorage — Logout löscht). */
export interface PortalSessionDocument {
  display_name: string;
  document_url: string;
  pb_record_id?: string;
  nextcloud_path?: string;
}

export interface PortalSessionState {
  version: 1;
  view: PortalView;
  document?: PortalSessionDocument | null;
  /** Letzter ARKO-Chip/Intent (Label, kein PII). */
  lastArkoIntent?: string | null;
}

export type PortalHomeRecentKind = 'document' | 'arko' | 'view';

export interface PortalHomeRecentItem {
  id: string;
  kind: PortalHomeRecentKind;
  label: string;
  sublabel: string;
  view?: PortalView;
  document?: PortalSessionDocument;
}

const PORTAL_SESSION_VERSION = 1 as const;

const RESTORABLE_VIEWS: PortalView[] = [
  'home',
  'chat',
  'dokumente',
  'diktat',
  'bausteine',
  'feedback',
  'litigation',
  'vertrags_qa',
  'contract_generator',
  'document_editor',
  'pdf_tools',
  'gedaechtnis',
  'matrix',
  'kanzlei_zugaenge',
];

export function portalSessionStorageKey(customerId: string): string {
  return `arkorbit-portal-session:v${PORTAL_SESSION_VERSION}:${customerId}`;
}

function isPortalView(value: unknown): value is PortalView {
  return typeof value === 'string' && RESTORABLE_VIEWS.includes(value as PortalView);
}

function isPortalSessionDocument(value: unknown): value is PortalSessionDocument {
  if (!value || typeof value !== 'object') return false;
  const d = value as PortalSessionDocument;
  return (
    typeof d.display_name === 'string' &&
    d.display_name.trim().length > 0 &&
    typeof d.document_url === 'string' &&
    /^https?:\/\//i.test(d.document_url.trim())
  );
}

export function loadPortalSessionState(customerId: string): PortalSessionState | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(portalSessionStorageKey(customerId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const v = parsed as Partial<PortalSessionState>;
    const rawView = v.view;
    if (v.version !== PORTAL_SESSION_VERSION || rawView === undefined) return null;
    const restoredView: PortalView | null = isPortalView(rawView) ? rawView : null;
    if (!restoredView) return null;
    const doc = v.document === null || v.document === undefined
      ? undefined
      : isPortalSessionDocument(v.document)
        ? v.document
        : undefined;
    const lastArkoIntent =
      typeof v.lastArkoIntent === 'string' && v.lastArkoIntent.trim()
        ? v.lastArkoIntent.trim()
        : v.lastArkoIntent === null
          ? null
          : undefined;
    return { version: PORTAL_SESSION_VERSION, view: restoredView, document: doc, lastArkoIntent };
  } catch {
    return null;
  }
}

export function savePortalSessionState(
  customerId: string,
  partial: Partial<Omit<PortalSessionState, 'version'>>,
): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const prev = loadPortalSessionState(customerId);
    const next: PortalSessionState = {
      version: PORTAL_SESSION_VERSION,
      view: partial.view ?? prev?.view ?? 'home',
      document: partial.document !== undefined ? partial.document : prev?.document,
      lastArkoIntent:
        partial.lastArkoIntent !== undefined ? partial.lastArkoIntent : prev?.lastArkoIntent,
    };
    sessionStorage.setItem(portalSessionStorageKey(customerId), JSON.stringify(next));
  } catch {
    /* sessionStorage voll oder blockiert */
  }
}

export function clearPortalSessionState(customerId: string): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(portalSessionStorageKey(customerId));
  } catch {
    /* ignore */
  }
}

/** Bis zu 3 „Zuletzt“-Einträge für die Startseite — aus sessionStorage + optional recentDocs. */
export function buildPortalHomeRecents(
  customerId: string,
  recentDocs: Array<{
    display_name: string;
    document_url: string;
    pb_record_id?: string;
    nextcloud_path?: string;
  }>,
  options?: { lastArkoIntent?: string | null },
): PortalHomeRecentItem[] {
  const session = loadPortalSessionState(customerId);
  const lastArkoIntent = options?.lastArkoIntent ?? session?.lastArkoIntent;
  const items: PortalHomeRecentItem[] = [];
  const seen = new Set<string>();

  const push = (item: PortalHomeRecentItem) => {
    if (seen.has(item.id) || items.length >= 3) return;
    seen.add(item.id);
    items.push(item);
  };

  const sessionDoc = session?.document;
  const recentDoc = recentDocs[0];
  if (sessionDoc) {
    push({
      id: `doc-${sessionDoc.document_url}`,
      kind: 'document',
      label: sessionDoc.display_name,
      sublabel: 'Zuletzt geöffnet',
      view: 'document_editor',
      document: sessionDoc,
    });
  } else if (recentDoc) {
    push({
      id: `doc-${recentDoc.document_url}`,
      kind: 'document',
      label: recentDoc.display_name,
      sublabel: 'Zuletzt geöffnet',
      view: 'document_editor',
      document: {
        display_name: recentDoc.display_name,
        document_url: recentDoc.document_url,
        pb_record_id: recentDoc.pb_record_id,
        nextcloud_path: recentDoc.nextcloud_path,
      },
    });
  }

  const hadChat =
    session?.view === 'chat' ||
    session?.view === 'document_editor' ||
    Boolean(lastArkoIntent?.trim());
  if (hadChat) {
    push({
      id: 'arko-doc',
      kind: 'arko',
      label: session?.view === 'document_editor' ? 'ARKO am Dokument' : 'ARKO',
      sublabel: lastArkoIntent?.trim() || (session?.view === 'document_editor' ? 'Dokument öffnen für ARKO' : 'Kanzlei-Assistent'),
      view: session?.view === 'document_editor' ? 'dokumente' : 'chat',
    });
  }

  const lastView = session?.view;
  const hasDoc = Boolean(sessionDoc || recentDoc);
  if (
    lastView &&
    lastView !== 'home' &&
    lastView !== 'chat' &&
    lastView !== 'document_editor' &&
    !hasDoc
  ) {
    push({
      id: `view-${lastView}`,
      kind: 'view',
      label: portalViewTitle(lastView),
      sublabel: 'Zuletzt besucht',
      view: lastView,
    });
  }

  return items;
}

/** Views, die nach Reload wiederhergestellt werden dürfen (Premium-Gates). */
export function canRestorePortalView(
  view: PortalView,
  ctx: PortalNavContext,
): boolean {
  if (view === 'document_editor' || view === 'litigation' || view === 'vertrags_qa') {
    return ctx.isPremium;
  }
  if (view === 'contract_generator') {
    return ctx.isPremium && ctx.contractGeneratorEnabled;
  }
  if (view === 'kanzlei_zugaenge') {
    return Boolean(ctx.teamAccessEnabled);
  }
  if (view === 'gedaechtnis') {
    return ctx.isPremium && Boolean(ctx.memcoreEnabled);
  }
  return RESTORABLE_VIEWS.includes(view);
}
